import { useMemo } from "react"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import Chip from "@mui/material/Chip"
import Divider from "@mui/material/Divider"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDNumberInput from "../MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import SectionCard from "../SectionCard"
import EmptyState from "./EmptyState"
import ApprovalStepTimeline from "./ApprovalStepTimeline"
import EmailChipInput from "./EmailChipInput"
import { EDITOR_STEPS } from "./RuleEditorStepper"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import {
  RULE_ENTITY_OPTIONS,
  RULE_CONDITION_FIELDS,
  RULE_CONDITION_OPERATORS,
  REGION_OPTIONS,
  APPROVER_OPTIONS,
  URGENCY_OPTIONS,
  valueTypeFor,
  IN_OPERATOR_VALUE_SEPARATOR,
  findApproverOption,
} from "../../constants"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { departmentList } from "../payment/create/NonPoPaymentBuilder"

const ENTITY_LABEL = RULE_ENTITY_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label
  return acc
}, {})

// "Step N of 5" eyebrow, derived from the shared step order.
const stepEyebrow = (key) => {
  const idx = EDITOR_STEPS.findIndex((s) => s.key === key)
  return idx === -1 ? undefined : `Step ${idx + 1} of ${EDITOR_STEPS.length}`
}

// Small, consistent helper line rendered under a form field.
const FieldHelp = ({ children }) => (
  <MDTypography
    variant="caption"
    color="text"
    display="block"
    mt={0.5}
    sx={{ opacity: 0.8 }}
  >
    {children}
  </MDTypography>
)

const RuleEditor = ({ rule, onChange, step, invalidNotifierEmail }) => {
  const ruleData = rule.rule || rule
  const conditions = rule.conditions || []
  const steps = rule.steps || []

  // Real budget categories (real numeric `id`, not the hand-picked slug list
  // used elsewhere for report/list filters) — a "category" condition must
  // compare against the actual budget_category.id the backend evaluates
  // rules with.
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )
  // String ids: "in" values round-trip as one comma-joined string.
  const { data: departmentsRaw } = useGetDepartmentsQuery()
  const departmentOptions = useMemo(
    () =>
      departmentList(departmentsRaw).map((d) => ({
        value: String(d.teamId),
        label: d.region ? `${d.teamName} (${d.region})` : d.teamName,
      })),
    [departmentsRaw]
  )
  const { data: centersPage } = useGetProcurementCentersQuery({ size: 200 })
  const centerOptions = useMemo(
    () =>
      (centersPage?.items || []).map((c) => ({
        value: String(c.centerId),
        label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
      })),
    [centersPage]
  )

  const setRuleField = (patch) =>
    onChange({ ...rule, rule: { ...ruleData, ...patch } })
  const setTop = (patch) => onChange({ ...rule, ...patch })

  const setCondition = (idx, patch) =>
    setTop({
      conditions: conditions.map((c, i) =>
        i === idx ? { ...c, ...patch } : c
      ),
    })
  const addCondition = () =>
    setTop({
      conditions: [
        ...conditions,
        { field: "category", operator: "=", value: "", valueType: valueTypeFor("category") },
      ],
    })
  const removeCondition = (idx) =>
    setTop({ conditions: conditions.filter((_, i) => i !== idx) })

  const setStep = (idx, patch) =>
    setTop({
      steps: steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    })
  const addStep = () =>
    setTop({
      steps: [
        ...steps,
        {
          stepOrder: steps.length + 1,
          approverType: APPROVER_OPTIONS[0]?.type || "role",
          approverRef: APPROVER_OPTIONS[0]?.value,
          mode: "sequential",
          quorum: 1,
          slaHours: 24,
          escalateTo: null,
          maxAmount: null,
        },
      ],
    })
  const removeStep = (idx) =>
    setTop({ steps: steps.filter((_, i) => i !== idx) })
  const reorderSteps = (fromIdx, toIdx) => {
    const next = [...steps]
    const [moved] = next.splice(fromIdx, 1)
    next.splice(toIdx, 0, moved)
    setTop({ steps: next.map((s, i) => ({ ...s, stepOrder: i + 1 })) })
  }

  // The backend stores a limit per (approverType, approverRef) pair, not per
  // step — if two steps share an approver, setting the limit on one applies
  // to both. Keep every step for that pair in sync so the editor never shows
  // a value that the save would silently overwrite.
  const setApproverMaxAmount = (approverType, approverRef, maxAmount) =>
    setTop({
      steps: steps.map((s) =>
        s.approverType === approverType && s.approverRef === approverRef
          ? { ...s, maxAmount }
          : s
      ),
    })

  // One row per distinct approver across the configured steps, in first-seen
  // order — mirrors how the backend scopes the limit.
  const approverLimitRows = useMemo(() => {
    const seen = new Set()
    const rows = []
    steps.forEach((s) => {
      const key = `${s.approverType}:${s.approverRef}`
      if (seen.has(key)) return
      seen.add(key)
      rows.push(s)
    })
    return rows
  }, [steps])

  // Values here must match what the entity actually sends, not just read
  // nicely — Order Request submits urgency uppercased (see
  // OrderRequestFormContainer: `form.urgency.toUpperCase()`), so "urgent"
  // never matches a saved condition unless the option value is "URGENT".
  const valueOptionsFor = (field) => {
    if (field === "region") return REGION_OPTIONS
    if (field === "category") return categoryOptions
    if (field === "department") return departmentOptions
    if (field === "center") return centerOptions
    if (field === "urgency")
      return URGENCY_OPTIONS.map((o) => ({ value: o.value.toUpperCase(), label: o.label }))
    return null
  }

  const entityName = ENTITY_LABEL[ruleData.entityType] || ruleData.entityType

  if (step === "details") {
    return (
      <SectionCard
        eyebrow={stepEyebrow("details")}
        title="Rule Details"
        subtitle="Name your rule and define its scope — which documents it applies to."
        icon="description"
      >
        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12}>
            {/* Capped so the name field does not stretch across a wide screen */}
            <MDBox sx={{ maxWidth: { xl: 640 } }}>
              <MDInput
                fullWidth
                required
                labelType="non-sticky"
                labelText="Rule Name"
                placeholder="e.g. Standard purchases ≤ 50M"
                value={ruleData.name || ""}
                onChange={(e) => setRuleField({ name: e.target.value })}
              />
              <FieldHelp>
                Choose a clear and descriptive name for this rule.
              </FieldHelp>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} xxl={4}>
            <MDSelect
              required
              labelType="non-sticky"
              labelText="Applies to"
              options={RULE_ENTITY_OPTIONS}
              value={
                RULE_ENTITY_OPTIONS.find((o) => o.value === ruleData.entityType) || null
              }
              onChange={(o) => setRuleField({ entityType: o?.value || "order" })}
            />
            <FieldHelp>Select the document type this rule applies to.</FieldHelp>
          </Grid>
        </Grid>
      </SectionCard>
    )
  }

  if (step === "conditions") {
    return (
      <SectionCard
        eyebrow={stepEyebrow("conditions")}
        title="Conditions"
        subtitle="Optional filters — when empty, this rule applies to all matching documents."
        icon="filter_alt"
        action={
          conditions.length > 0 ? (
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addCondition}
            >
              + Add condition
            </MDButton>
          ) : null
        }
      >
        {conditions.length === 0 ? (
          <EmptyState
            icon="filter_alt"
            title="No conditions yet"
            description={`Without conditions, this rule applies to all ${entityName} documents within the scope you set in Rule Details.`}
            example="Category = IT Equipment AND Amount ≤ 50,000,000"
            actionLabel="+ Add condition"
            onAction={addCondition}
          />
        ) : (
          conditions.map((c, idx) => {
            const valueOpts = valueOptionsFor(c.field)
            return (
              <MDBox key={idx} bgColor="grey-100" borderRadius="lg" p={1.5} mb={1.5}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <MDSelect
                      labelType="non-sticky"
                      labelText="Field"
                      options={RULE_CONDITION_FIELDS}
                      value={
                        RULE_CONDITION_FIELDS.find((o) => o.value === c.field) ||
                        null
                      }
                      onChange={(o) =>
                        setCondition(idx, {
                          field: o?.value,
                          value: "",
                          valueType: valueTypeFor(o?.value),
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <MDSelect
                      labelType="non-sticky"
                      labelText="Compare"
                      options={RULE_CONDITION_OPERATORS}
                      value={
                        RULE_CONDITION_OPERATORS.find(
                          (o) => o.value === c.operator
                        ) || null
                      }
                      onChange={(o) => setCondition(idx, { operator: o?.value })}
                    />
                  </Grid>
                  <Grid item xs={6} md={5}>
                    {c.operator === "in" ? (
                      valueOpts ? (
                        <MDSelect
                          isMulti
                          displayChips
                          labelType="non-sticky"
                          labelText="Values"
                          placeholder="Select…"
                          options={valueOpts}
                          // Wire format is ONE comma-separated string
                          // ("1,2,5"), never a JSON array — split/join here
                          // so the multi-select only ever sees option objects.
                          value={valueOpts.filter((o) =>
                            String(c.value || "")
                              .split(IN_OPERATOR_VALUE_SEPARATOR)
                              .map((v) => v.trim())
                              .includes(String(o.value))
                          )}
                          onChange={(selected) =>
                            setCondition(idx, {
                              value: (selected || [])
                                .map((o) => o.value)
                                .join(IN_OPERATOR_VALUE_SEPARATOR),
                            })
                          }
                        />
                      ) : (
                        <MDBox>
                          <MDInput
                            fullWidth
                            labelType="non-sticky"
                            labelText="Values"
                            placeholder="e.g. 1,2,5"
                            value={c.value || ""}
                            onChange={(e) => setCondition(idx, { value: e.target.value })}
                          />
                          <FieldHelp>Comma-separated, no brackets — e.g. 1,2,5</FieldHelp>
                        </MDBox>
                      )
                    ) : valueOpts ? (
                      <MDSelect
                        labelType="non-sticky"
                        labelText="Value"
                        placeholder="Select…"
                        options={valueOpts}
                        // Loaded rules can round-trip a NUMBER condition's
                        // value as a numeric string (e.g. categoryId "3")
                        // even though the option list's `value` (from the
                        // real category/region API) is a number — compare
                        // loosely by string so an existing rule's saved
                        // category still shows as selected.
                        value={
                          valueOpts.find((o) => String(o.value) === String(c.value)) || null
                        }
                        onChange={(o) => setCondition(idx, { value: o?.value })}
                      />
                    ) : c.field === "amount" ? (
                      <MDNumberInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="Value"
                        value={c.value ?? ""}
                        onChange={(v) => setCondition(idx, { value: v })}
                      />
                    ) : (
                      <MDInput
                        fullWidth
                        labelType="non-sticky"
                        labelText="Value"
                        value={c.value || ""}
                        onChange={(e) =>
                          setCondition(idx, { value: e.target.value })
                        }
                      />
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={1}
                    sx={{ textAlign: { xs: "right", md: "center" } }}
                  >
                    <MDButton
                      size="small"
                      variant="text"
                      color="error"
                      iconOnly
                      onClick={() => removeCondition(idx)}
                    >
                      <Icon>delete</Icon>
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            )
          })
        )}
      </SectionCard>
    )
  }

  if (step === "steps") {
    return (
      <SectionCard
        eyebrow={stepEyebrow("steps")}
        title="Approval Steps"
        subtitle="Build the approval chain in order — drag steps to reorder."
        icon="account_tree"
        action={
          steps.length > 0 ? (
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={addStep}
            >
              + Add step
            </MDButton>
          ) : null
        }
      >
        {steps.length === 0 ? (
          <EmptyState
            icon="account_tree"
            title="No approval steps yet"
            description="Add approvers in order. Each step can need one approver or several at once."
            example="1. Procurement → 2. Procurement Manager → 3. Finance Team"
            actionLabel="+ Add first step"
            onAction={addStep}
          />
        ) : (
          <ApprovalStepTimeline
            steps={steps}
            conditions={conditions}
            approverOptions={APPROVER_OPTIONS}
            onChangeStep={setStep}
            onRemoveStep={removeStep}
            onReorder={reorderSteps}
          />
        )}

        <Divider sx={{ my: 3 }} />

        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
          Notifiers
        </MDTypography>
        <MDBox sx={{ maxWidth: { xl: 640 } }}>
          <EmailChipInput
            value={ruleData.notifierEmails || ""}
            onChange={(next) => setRuleField({ notifierEmails: next })}
            placeholder="name1@company.com, name2@company.com"
            invalidEmails={invalidNotifierEmail ? [invalidNotifierEmail] : []}
          />
          <FieldHelp>
            These people are copied on every notification for this rule but don&apos;t approve
            anything. Type an address then comma / space / Enter to confirm it.
          </FieldHelp>
        </MDBox>
      </SectionCard>
    )
  }

  if (step === "doa") {
    return (
      <SectionCard
        eyebrow={stepEyebrow("doa")}
        title="Approval Limits"
        subtitle="Set the highest amount each approver in this rule can approve."
        icon="grid_on"
      >
        {approverLimitRows.length === 0 ? (
          <EmptyState
            icon="grid_on"
            title="No approvers yet"
            description="Add approval steps first — limits are set per approver once the chain is built."
            example="Center Admin ≤ 20M · Procurement Manager ≤ 50M"
          />
        ) : (
          approverLimitRows.map((s) => {
            const isEmails = s.approverType === "emails"
            const selectedApprover = findApproverOption(s)
            const approverLabel = isEmails
              ? null
              : selectedApprover?.label || s.approverRef
            const emailChips = isEmails
              ? (s.approverRef || "")
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean)
              : []
            const sharedStepCount = steps.filter(
              (x) => x.approverType === s.approverType && x.approverRef === s.approverRef
            ).length

            return (
              <MDBox
                key={`${s.approverType}:${s.approverRef}`}
                bgColor="grey-100"
                borderRadius="lg"
                p={1.5}
                mb={1.5}
              >
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={7} sx={{ minWidth: 0 }}>
                    {isEmails ? (
                      emailChips.length > 0 ? (
                        <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                          {emailChips.map((email) => (
                            <Chip
                              key={email}
                              label={email}
                              size="small"
                              sx={{
                                maxWidth: "100%",
                                "& .MuiChip-label": {
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          ))}
                        </MDBox>
                      ) : (
                        <MDTypography variant="button" fontWeight="medium" display="block">
                          No email added yet
                        </MDTypography>
                      )
                    ) : (
                      <MDTypography variant="button" fontWeight="medium" display="block">
                        {approverLabel}
                      </MDTypography>
                    )}
                    {sharedStepCount > 1 && (
                      <FieldHelp>
                        Applies to all {sharedStepCount} steps this approver is on.
                      </FieldHelp>
                    )}
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <MDNumberInput
                      fullWidth
                      labelType="non-sticky"
                      labelText="Highest amount they can approve"
                      placeholder="No limit"
                      value={s.maxAmount ?? ""}
                      onChange={(v) =>
                        setApproverMaxAmount(s.approverType, s.approverRef, v === "" ? null : Number(v))
                      }
                    />
                  </Grid>
                </Grid>
              </MDBox>
            )
          })
        )}
      </SectionCard>
    )
  }

  return null
}

export default RuleEditor
